import type { HypothesisPhase } from '../../types';

/**
 * The phase picker's options, in **one** place (build unit U-13c).
 *
 * ── Why this was extracted ──────────────────────────────────────────────────
 *
 * PRE-3's add panel had this list privately, and FLAG-3's promote needs the same one: §3.2's source-2
 * rule derives `discoveryBucket` **from `phase`**, and the mapping is *"a total function over the
 * `phase` enum, closed by `assertNever`, so adding a phase later breaks the build rather than
 * silently producing an unreachable hypothesis."*
 *
 * A second copy would defeat exactly that. `assertNever` guards the *mapping*; nothing guards a
 * **picker** that offers six of seven phases — the seventh simply becomes unreachable, and the
 * pattern that needed it gets filed on the wrong Discovery page with no error anywhere. The
 * `satisfies` clause below closes that gap the same way the mapping closes its own: a phase added to
 * the enum and not to this list is a compile error.
 *
 * Order is reading order and is deliberately the shape of a match rather than alphabetical: the two
 * possession phases, then the two transitions, then the three specifics.
 */
export interface PhaseOption {
  readonly phase: HypothesisPhase;
  readonly label: string;
}

export const PHASE_OPTIONS = [
  { phase: 'inPossession', label: 'In possession' },
  { phase: 'outOfPossession', label: 'Out of possession' },
  { phase: 'attTransition', label: 'Attacking transition' },
  { phase: 'defTransition', label: 'Defensive transition' },
  { phase: 'setPiece', label: 'Set piece' },
  { phase: 'individual', label: 'Individual' },
  { phase: 'gk', label: 'Goalkeeper' },
] as const satisfies readonly PhaseOption[];

/**
 * Every `HypothesisPhase` is offered — asserted at compile time, not by counting rows.
 *
 * `satisfies readonly PhaseOption[]` above catches a *wrong* value; this catches a **missing** one,
 * which is the failure that has no runtime symptom. The type is `never` when the two sets agree.
 */
type _EveryPhaseIsOffered = Exclude<HypothesisPhase, (typeof PHASE_OPTIONS)[number]['phase']>;
const _everyPhaseIsOffered: _EveryPhaseIsOffered[] = [];
void _everyPhaseIsOffered;
